import type { Duration, GuestKind, Mode, PassOptions } from './availability';
import { endOfMoscowDay } from './moscowTime';

/** «Такси или курьер в один тап» (ideas 2026-10-13 #5): a short one-time pass with no extra fields. */
export type QuickKind = 'taxi' | 'courier';

export const QUICK_MINUTES = 60;
const MINUTE = 60_000;

const PURPOSE: Record<QuickKind, RegExp> = {
  taxi: /такси/i,
  courier: /курьер|доставк/i,
};

/** The object's visit purpose that matches the kind ("Такси", "Курьер", "Доставка"), if there is one. */
export function quickPurpose<T extends { id: number; name: string }>(kind: QuickKind, options: readonly T[]): T | null {
  return options.find((o) => PURPOSE[kind].test(o.name)) ?? null;
}

/**
 * How the quick pass is ordered: a taxi always needs the car; a courier comes on foot when the object allows
 * it and falls back to a car otherwise. One-time first; a temporary pass of the same short window is the fallback.
 */
export function quickPlan(kind: QuickKind, opts: PassOptions): { guest: GuestKind; duration: Duration; mode: Mode } | null {
  const guests: GuestKind[] = kind === 'taxi' ? ['vehicle'] : ['pedestrian', 'vehicle'];
  for (const guest of guests) {
    const list = opts.durations(guest);
    const pick = list.find((d) => d.duration === 'oneTime') ?? list[0];
    if (pick) return { guest, ...pick };
  }
  return null;
}

/** From now for an hour, never past midnight (a one-time pass is a one-day pass) or the object limit. */
export function quickWindow(now: Date, limitHours: number | null, forRequest = false): { startsAt: Date; expiresAt: Date } {
  // Approval requests must start in the future (see passWindow).
  const startsAt = new Date(Math.floor(now.getTime() / MINUTE) * MINUTE + (forRequest ? MINUTE : 0));
  const ends = [new Date(startsAt.getTime() + QUICK_MINUTES * MINUTE), endOfMoscowDay(startsAt)];
  if (limitHours) ends.push(new Date(startsAt.getTime() + limitHours * 60 * MINUTE));
  return { startsAt, expiresAt: new Date(Math.min(...ends.map((d) => d.getTime()))) };
}
